import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, ExternalLink, Monitor, Smartphone } from "lucide-react";
import { Button, Tabs } from "@/components/ui";
import { cn } from "@/lib/utils";
import { publicWebOrigin } from "@/utils";

const MOBILE_PREVIEW_WIDTH = 390;

type PreviewDevice = "web" | "mobile";

export const useLivePreviewIframe = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIFrameConnected, setIsIFrameConnected] = useState(0);
  const iframeOrigin = useMemo(() => publicWebOrigin(), []);

  const postToPreview = useCallback(
    (message: Record<string, unknown>) => {
      if (!isIFrameConnected || !iframeRef.current?.contentWindow) return;
      iframeRef.current.contentWindow.postMessage(message, iframeOrigin);
    },
    [isIFrameConnected, iframeOrigin]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      setIsIFrameConnected(count => count + 1);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeOrigin]);

  return {
    iframeRef,
    iframeOrigin,
    postToPreview,
    onIframeLoad: () => setIsIFrameConnected(count => count + 1),
  };
};

interface LivePreviewFrameProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  iframeOrigin: string;
  srcPath: string;
  title: string;
  openPageAriaLabel?: string;
  shareUrl?: string;
  onCopyShareUrl?: () => void;
  onOpenPage?: () => void;
  onIframeLoad?: () => void;
  className?: string;
}

export const LivePreviewFrame: React.FC<LivePreviewFrameProps> = ({
  iframeRef,
  iframeOrigin,
  srcPath,
  title,
  openPageAriaLabel = "Open page",
  shareUrl,
  onCopyShareUrl,
  onOpenPage,
  onIframeLoad,
  className,
}) => {
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("web");
  const isMobilePreview = previewDevice === "mobile";

  return (
    <div
      className={cn("flex flex-col overflow-hidden rounded-2xl bg-slate-900 shadow-xl shadow-slate-900/15", className)}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Live preview</span>
        <Tabs
          value={previewDevice}
          onValueChange={value => setPreviewDevice(value as PreviewDevice)}
          className="ml-1 gap-0"
        >
          <Tabs.List className="h-7 bg-white/10">
            <Tabs.Trigger
              value="web"
              className="gap-1 px-2 text-white/70 hover:text-white data-active:text-slate-900"
              aria-label="Desktop preview"
            >
              <Monitor className="size-3.5" />
              <span className="hidden sm:inline">Web</span>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="mobile"
              className="gap-1 px-2 text-white/70 hover:text-white data-active:text-slate-900"
              aria-label="Mobile preview"
            >
              <Smartphone className="size-3.5" />
              <span className="hidden sm:inline">Mobile</span>
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs>
        <div className="flex-grow" />
        {shareUrl && onCopyShareUrl && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Copy share link"
            onClick={onCopyShareUrl}
          >
            <Copy className="size-3.5" />
          </Button>
        )}
        {onOpenPage && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 text-white/70 hover:bg-white/10 hover:text-white"
            aria-label={openPageAriaLabel}
            onClick={onOpenPage}
          >
            <ExternalLink className="size-3.5" />
          </Button>
        )}
      </div>
      <div
        className={cn(
          "min-h-0 min-w-0 w-full flex-1",
          isMobilePreview ? "flex justify-center bg-black/25 p-3" : "flex flex-col"
        )}
      >
        <iframe
          ref={iframeRef}
          src={`${iframeOrigin}${srcPath}`}
          title={title}
          className={cn(
            "min-h-0 bg-white overscroll-contain",
            isMobilePreview ? "h-full max-w-full rounded-xl" : "h-full w-full flex-1 rounded-t-lg"
          )}
          style={isMobilePreview ? { width: `min(${MOBILE_PREVIEW_WIDTH}px, 100%)` } : undefined}
          onLoad={onIframeLoad}
        />
      </div>
    </div>
  );
};
